import { create } from 'zustand'
import { getStoreAuthHeaders } from '../utils/auth'
import { API_BASE } from '../utils/config'

export interface DamageType {
  id: string;
  label: string;
  order: number;
}

interface DamagesState {
  damages: DamageType[];
  damagesLoading: boolean;
  damagesError: string | null;

  _authHeader: () => Record<string, string>;
  fetchDamages: () => Promise<void>;
  addDamage: (label: string) => Promise<void>;
  updateDamage: (id: string, label: string) => Promise<void>;
  removeDamage: (id: string) => Promise<void>;
  reorderDamages: (orderedDamages: DamageType[]) => Promise<void>;
}

// Fallback pré-intégré au cas où la BDD est vide ou hors-ligne lors du 1er lancement
const FALLBACK_DAMAGES: DamageType[] = [
  { id: '1', label: 'Rayure', order: 1 },
  { id: '2', label: 'Impact / Brisé', order: 2 },
  { id: '3', label: 'Enfoncement', order: 3 },
  { id: '4', label: 'Pièce manquante', order: 4 },
  { id: '5', label: 'Tache', order: 5 },
  { id: '6', label: 'Pièce cassée / Fendue', order: 6 },
]

export const useDamagesStore = create<DamagesState>()((set, get) => ({
  damages: FALLBACK_DAMAGES,
  damagesLoading: false,
  damagesError: null,

  // ── Auth header — via store Zustand (plus de localStorage hack) ────────
  _authHeader: (): Record<string, string> => {
    try {
      return getStoreAuthHeaders()
    } catch {
      return {}
    }
  },

  fetchDamages: async () => {
    set({ damagesLoading: true, damagesError: null })
    try {
      const res = await fetch(`${API_BASE}/config/damages`, {
        headers: get()._authHeader()
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      
      // On fait confiance au backend (qui s'auto-gère maintenant)
      if (json.data) {
        set({ damages: json.data, damagesLoading: false, damagesError: null })
      } else {
        set({ damagesLoading: false })
      }
    } catch (e: any) {
      console.warn('[DamagesStore] Erreur fetch damages :', e.message)
      set({ damagesLoading: false, damagesError: e.message })
      // On garde les FALLBACK localement uniquement en cas d'erreur réseau totale
    }
  },

  addDamage: async (label: string) => {
    try {
      const res = await fetch(`${API_BASE}/config/damages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...get()._authHeader()
        },
        body: JSON.stringify({ label })
      })
      if (!res.ok) throw new Error(`Erreur ajout (${res.status})`)
      await get().fetchDamages()
    } catch (e: any) {
      set({ damagesError: e.message })
    }
  },

  updateDamage: async (id: string, label: string) => {
    // Maj optimiste
    set(s => ({ damages: s.damages.map(d => d.id === id ? { ...d, label } : d) }))
    try {
      const res = await fetch(`${API_BASE}/config/damages/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...get()._authHeader()
        },
        body: JSON.stringify({ label })
      })
      if (!res.ok) throw new Error('Échec mise à jour')
    } catch (e: any) {
      await get().fetchDamages() // rollback
      set({ damagesError: e.message })
    }
  },

  removeDamage: async (id: string) => {
    // Delete optimiste
    set(s => ({ damages: s.damages.filter(d => d.id !== id) }))
    try {
      const res = await fetch(`${API_BASE}/config/damages/${id}`, {
        method: 'DELETE',
        headers: get()._authHeader()
      })
      if (!res.ok) throw new Error('Échec suppression')
    } catch (e: any) {
      await get().fetchDamages() // rollback
      set({ damagesError: e.message })
    }
  },

  reorderDamages: async (ordered: DamageType[]) => {
    const minOrder = 1;
    const reindexed = ordered.map((d, i) => ({ ...d, order: minOrder + i }));
    // Maj optimiste
    set({ damages: reindexed })

    try {
      const payload = reindexed.map(d => ({ id: d.id, order: d.order }))
      const res = await fetch(`${API_BASE}/config/damages/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...get()._authHeader()
        },
        body: JSON.stringify({ items: payload })
      })
      if (!res.ok) throw new Error('Échec réordonnancement')
    } catch (e: any) {
      await get().fetchDamages() // rollback
      set({ damagesError: e.message })
    }
  }
}))
